import { createDeck, drawCardFromDeck } from "./deck.js";
import { getRankValue, isSameCard } from "./card.js";
import {
  chooseCpuAction,
  normalizeCpuDifficulty,
  refreshCpuMemoryForAll,
  rememberDiscardedCards,
  rememberHumanRearrangedCards,
  rememberHumanWastepilePickup
} from "./cpu.js";
import { RANKS, SUIT_SYMBOLS } from "./constants.js";
import {
  DEFAULT_CPU_DIFFICULTY,
  PLAYER_ROLE_CPU,
  PLAYER_ROLE_HUMAN,
  clearTableSlotCard,
  createPlayer,
  findCardInPlayerCards,
  getPlayableCards,
  getPlayableZone,
  hasAnyCards,
  isCpuPlayer,
  onlyHasFaceDownCards,
  replaceTableSlotFaceUpCard,
  resetTableSlots,
  swapTableSlotFaceUpCards
} from "./player.js";

const MIN_PLAYERS = 2;
const MAX_PLAYERS = 4;
const RANK_RESET = "2";
const RANK_CLEAR = "10";
const CPU_ACTION_LOOP_LIMIT = 24;
const LOG_LIMIT = 500;
const PHASE_IDLE = "idle";
const PHASE_REARRANGING = "rearranging";
const PHASE_PLAYING = "playing";
const PLAYER_NAME_TAG_PATTERN = /<[^>]*>/g;
const PLAYER_NAME_WHITESPACE_PATTERN = /\s+/g;
const STARTING_COMPARISON_RANKS = [...RANKS].sort((a, b) => getStartingComparisonRankValue(a) - getStartingComparisonRankValue(b));

export function createGame() {
  return {
    players: [],
    playStack: [],
    discardStack: [],
    deck: [],
    initialDeck: [],
    started: false,
    phase: PHASE_IDLE,
    currentPlayerIndex: -1,
    nextCardMustBeSameRank: false,
    canDrawChanceCard: true,
    canPickUpWastepile: true,
    lastChanceCard: null,
    activeFaceDownCard: null,
    cardsPlayedInTurn: 0,
    log: [],
    suppressLogMessages: false
  };
}

export function sanitizePlayerName(name) {
  if (typeof name !== "string") {
    return "";
  }

  return name
    .replace(PLAYER_NAME_TAG_PATTERN, " ")
    .replace(/[<>]/g, "")
    .replace(PLAYER_NAME_WHITESPACE_PATTERN, " ")
    .trim();
}

export function addPlayer(state, name, options = {}) {
  if (name == null) {
    throw new Error("The player name is null. Invalid argument.");
  }

  const sanitizedName = sanitizePlayerName(name);
  if (sanitizedName === "") {
    return false;
  }
  if (state.players.length >= MAX_PLAYERS) {
    return false;
  }

  const role = options.role === PLAYER_ROLE_CPU ? PLAYER_ROLE_CPU : PLAYER_ROLE_HUMAN;
  const cpuDifficulty = role === PLAYER_ROLE_CPU
    ? normalizeCpuDifficulty(options.cpuDifficulty ?? options.difficulty ?? DEFAULT_CPU_DIFFICULTY)
    : null;

  state.players.push(createPlayer(sanitizedName, { role, cpuDifficulty }));
  return true;
}

export function startGame(state, options = {}) {
  if (state.phase !== PHASE_IDLE || state.started) {
    return false;
  }
  if (state.players.length < MIN_PLAYERS) {
    throw new Error(`At least ${MIN_PLAYERS} players required.`);
  }

  state.deck = createDeck(options);
  state.initialDeck = state.deck.map(cloneCardSpec);
  state.currentPlayerIndex = 0;
  for (const player of state.players) {
    drawToHand(player, state);
    drawToHand(player, state);
    drawToHand(player, state);

    player.faceUp = [drawCardFromDeck(state.deck), drawCardFromDeck(state.deck), drawCardFromDeck(state.deck)]
      .filter(Boolean);
    player.faceDown = [drawCardFromDeck(state.deck), drawCardFromDeck(state.deck), drawCardFromDeck(state.deck)]
      .filter(Boolean);
    resetTableSlots(player);
  }
  if (options.rearrangeBeforeStart) {
    state.started = false;
    state.phase = PHASE_REARRANGING;
  } else {
    beginPlayingPhase(state);
  }
  refreshCpuMemoryForAll(state);
  return true;
}

export function getInitialDeckSnapshot(state) {
  if (!Array.isArray(state?.initialDeck)) {
    return [];
  }
  return state.initialDeck.map(cloneCardSpec);
}

export function swapSetupCards(state, { playerName, handCardId, faceUpCardId, otherFaceUpCardId }) {
  if (state.phase !== PHASE_REARRANGING) {
    return { ok: false, message: "Setup swaps are only allowed before play starts." };
  }

  const current = getCurrentPlayer(state);
  if (!current || current.name !== playerName) {
    return { ok: false, message: "Only the starting player can rearrange setup cards." };
  }

  const player = getPlayerByName(state, playerName);
  if (!player) {
    return { ok: false, message: "Player not found." };
  }

  const faceUpIndex = player.faceUp.findIndex((card) => card.id === faceUpCardId);
  const isHandSwap = handCardId != null && otherFaceUpCardId == null;
  const isFaceUpReorder = handCardId == null && otherFaceUpCardId != null;
  if (!isHandSwap && !isFaceUpReorder) {
    return { ok: false, message: "Choose one hand card and one face-up table card, or two face-up table cards." };
  }
  if (faceUpIndex < 0) {
    return { ok: false, message: "Choose a face-up table card." };
  }

  if (isFaceUpReorder) {
    const otherFaceUpIndex = player.faceUp.findIndex((card) => card.id === otherFaceUpCardId);
    if (otherFaceUpIndex < 0 || otherFaceUpIndex === faceUpIndex) {
      return { ok: false, message: "Choose two different face-up table cards." };
    }

    const faceUpCard = player.faceUp[faceUpIndex];
    const otherFaceUpCard = player.faceUp[otherFaceUpIndex];
    const swappedSlots = swapTableSlotFaceUpCards(
      player,
      faceUpCard.id,
      otherFaceUpCard.id,
      faceUpIndex,
      otherFaceUpIndex
    );
    if (!swappedSlots) {
      return { ok: false, message: "Could not rearrange those face-up table cards." };
    }

    player.faceUp[faceUpIndex] = otherFaceUpCard;
    player.faceUp[otherFaceUpIndex] = faceUpCard;
    refreshCpuMemoryForAll(state);
    return { ok: true, message: "Face-up cards rearranged." };
  }

  const handIndex = player.hand.findIndex((card) => card.id === handCardId);
  if (handIndex < 0) {
    return { ok: false, message: "Choose one hand card and one face-up table card." };
  }

  const handCard = player.hand[handIndex];
  const faceUpCard = player.faceUp[faceUpIndex];
  if (!isCpuPlayer(player)) {
    rememberHumanRearrangedCardsForCpuPlayers(state, [faceUpCard]);
  }
  replaceTableSlotFaceUpCard(player, faceUpCard.id, handCard.id, faceUpIndex);
  player.hand[handIndex] = faceUpCard;
  player.faceUp[faceUpIndex] = handCard;
  refreshCpuMemoryForAll(state);
  return { ok: true, message: "Setup cards swapped." };
}

export function readyForPlay(state, { playerName }) {
  if (state.phase !== PHASE_REARRANGING) {
    return { ok: false, message: "Game is not waiting for ready." };
  }

  const current = getCurrentPlayer(state);
  if (!current || current.name !== playerName) {
    return { ok: false, message: "Only the starting player can ready the game." };
  }

  beginPlayingPhase(state);
  refreshCpuMemoryForAll(state);
  return { ok: true, message: "Ready." };
}

export function autoArrangeSetupCards(state, { playerName }) {
  if (state.phase !== PHASE_REARRANGING) {
    return { ok: false, message: "Setup arrangement is only allowed before play starts." };
  }

  const player = getPlayerByName(state, playerName);
  if (!player) {
    return { ok: false, message: "Player not found." };
  }

  const faceUpCount = player.faceUp.length;
  const handCount = player.hand.length;
  const previousFaceUpIds = new Set(player.faceUp.map((card) => card.id));
  const arrangedCards = [...player.hand, ...player.faceUp].sort(compareSetupCardStrength);
  player.faceUp = arrangedCards.slice(0, faceUpCount);
  player.hand = arrangedCards.slice(faceUpCount, faceUpCount + handCount);
  resetTableSlots(player);
  if (!isCpuPlayer(player)) {
    rememberHumanRearrangedCardsForCpuPlayers(
      state,
      player.hand.filter((card) => previousFaceUpIds.has(card.id))
    );
  }
  refreshCpuMemoryForAll(state);
  return { ok: true, message: "Setup cards arranged." };
}

export function getCurrentPlayer(state) {
  if (state.currentPlayerIndex < 0) {
    return null;
  }
  return state.players[state.currentPlayerIndex] ?? null;
}

export function getPlayerByName(state, playerName) {
  return state.players.find((player) => player.name === playerName) ?? null;
}

export function getHumanPlayer(state) {
  return state.players.find((player) => player.role === PLAYER_ROLE_HUMAN) ?? null;
}

export function getCpuPlayers(state) {
  return state.players.filter((player) => isCpuPlayer(player));
}

export function isCpuTurn(state) {
  if (!isPlayingPhase(state) || isGameOver(state)) {
    return false;
  }

  const current = getCurrentPlayer(state);
  return Boolean(current && current.status === "Playing" && isCpuPlayer(current));
}

export function determineStartingPlayer(players, fallbackPlayer = null) {
  const candidates = Array.isArray(players) ? players : [];
  const fallback = candidates.includes(fallbackPlayer)
    ? fallbackPlayer
    : (candidates[0] ?? null);

  for (const rank of STARTING_COMPARISON_RANKS) {
    let highestCount = 0;
    let rankWinner = null;
    let isTied = false;

    for (const player of candidates) {
      const hand = Array.isArray(player?.hand) ? player.hand : [];
      const count = hand.reduce((total, card) => total + (card.rank === rank ? 1 : 0), 0);

      if (count > highestCount) {
        highestCount = count;
        rankWinner = player;
        isTied = false;
      } else if (count > 0 && count === highestCount) {
        isTied = true;
      }
    }

    if (highestCount > 0 && !isTied) {
      return rankWinner;
    }
  }

  return fallback;
}

function getStartingComparisonRankValue(rank) {
  if (rank === RANK_CLEAR) {
    return 16;
  }
  if (rank === RANK_RESET) {
    return 15;
  }

  return getRankValue({ rank });
}

export function executeCpuAction(state, options = {}) {
  if (!isPlayingPhase(state)) {
    return { ok: false, message: "Game is not in play." };
  }
  if (isGameOver(state)) {
    return { ok: false, message: "Game has ended." };
  }

  const currentPlayer = getCurrentPlayer(state);
  if (!currentPlayer) {
    return { ok: false, message: "No active player." };
  }

  const playerName = options.playerName ?? currentPlayer.name;
  const player = assertCurrentPlayer(state, playerName);
  if (!isCpuPlayer(player)) {
    return { ok: false, message: "Current player is not a CPU player." };
  }
  if (player.status !== "Playing") {
    return { ok: false, message: "Current player is not an active CPU player." };
  }

  const decision = selectCpuDecision(state, player, options.rng ?? Math.random);
  if (!decision) {
    const turnResult = nextTurn(state);
    record(state, () => `${player.name} passed turn.`);
    const passResult = {
      ok: true,
      message: `${player.name} passed turn.`,
      decision: { type: "pass" }
    };
    if (turnResult?.wastePickup) {
      passResult.wastePickup = turnResult.wastePickup;
    }
    return passResult;
  }

  const result = applyCpuDecision(state, player, decision);
  const actionResult = {
    ok: result.ok,
    message: result.message,
    decision,
    result
  };
  if (result?.wastePickup) {
    actionResult.wastePickup = result.wastePickup;
  }
  return actionResult;
}

export function executeCpuTurn(state, options = {}) {
  if (!isPlayingPhase(state)) {
    return {
      ok: false,
      message: "Game is not in play.",
      steps: []
    };
  }
  if (isGameOver(state)) {
    return {
      ok: false,
      message: "Game has ended.",
      steps: []
    };
  }

  const startingPlayer = getCurrentPlayer(state);
  if (!startingPlayer || !isCpuPlayer(startingPlayer) || startingPlayer.status !== "Playing") {
    return {
      ok: false,
      message: "Current player is not a CPU player.",
      steps: []
    };
  }

  const steps = [];
  const maxSteps = options.maxSteps ?? CPU_ACTION_LOOP_LIMIT;

  for (let index = 0; index < maxSteps; index += 1) {
    if (isGameOver(state)) {
      break;
    }

    const current = getCurrentPlayer(state);
    if (!current || current.name !== startingPlayer.name || !isCpuPlayer(current) || current.status !== "Playing") {
      break;
    }

    const step = executeCpuAction(state, {
      playerName: startingPlayer.name,
      rng: options.rng
    });

    steps.push(step);

    if (!step.ok) {
      break;
    }
  }

  return {
    ok: steps.every((step) => step.ok),
    message: steps.at(-1)?.message ?? "No CPU action taken.",
    steps
  };
}

export function playCard(state, { playerName, cardId }) {
  if (!isPlayingPhase(state)) {
    return { ok: false, message: "Game is not in play." };
  }

  const player = assertCurrentPlayer(state, playerName);
  const cardInfo = findCardInPlayerCards(player, cardId);

  if (!cardInfo) {
    return { ok: false, message: "Card not found in player cards." };
  }
  if (cardInfo.zone === "faceDown") {
    throw new Error("Face-down cards cannot be played directly. Open one first.");
  }
  if (!isValidMove(state, player, cardInfo.card)) {
    return { ok: false, message: "Invalid move." };
  }

  if (state.lastChanceCard && state.lastChanceCard.id === cardInfo.card.id) {
    state.lastChanceCard = null;
  }

  if (cardInfo.zone === "faceUp") {
    clearTableSlotCard(player, "faceUp", cardInfo.card.id, cardInfo.index);
  }
  player[cardInfo.zone].splice(cardInfo.index, 1);
  state.playStack.unshift(cardInfo.card);
  record(state, () => `${player.name} played ${formatCard(cardInfo.card)} from ${formatZone(cardInfo.zone)}.`);
  drawToHand(player, state);
  resetTurnStateSoft(state);

  const hasWon = checkWinCondition(state, player);
  let extraTurn = false;

  if (cardInfo.card.rank === RANK_CLEAR || isFourOfAKind(state.playStack)) {
    rememberDiscardedCardsForCpuPlayers(state, state.playStack);
    state.discardStack.push(...state.playStack);
    state.playStack = [];
    extraTurn = true;
    record(state, () => `${player.name} cleared the wastepile.`);
  } else if (cardInfo.card.rank === RANK_RESET) {
    extraTurn = true;
    record(state, () => `${player.name} activated reset with ${formatCard(cardInfo.card)}.`);
  } else if (hasCardsOfSameRank(player, cardInfo.card)) {
    state.nextCardMustBeSameRank = true;
    extraTurn = true;
    record(state, () => `${player.name} may continue same rank (${cardInfo.card.rank}).`);
  }

  if (onlyHasFaceDownCards(player)) {
    extraTurn = true;
  }

  let turnResult = null;
  if (!extraTurn || hasWon) {
    turnResult = nextTurn(state);
  }

  refreshCpuMemoryForAll(state);
  const playResult = { ok: true, message: "Card played." };
  if (turnResult?.wastePickup) {
    playResult.wastePickup = turnResult.wastePickup;
  }
  return playResult;
}

export function drawChanceCard(state, { playerName }) {
  if (!isPlayingPhase(state)) {
    return { ok: false, message: "Game is not in play." };
  }

  const player = assertCurrentPlayer(state, playerName);
  if (!state.canDrawChanceCard) {
    return { ok: false, message: "Chance card already used this turn." };
  }
  if (state.nextCardMustBeSameRank) {
    return { ok: false, message: "Cannot draw chance card during same-rank continuation." };
  }

  const drawnCard = drawCardFromDeck(state.deck);
  if (!drawnCard) {
    return { ok: false, message: "Deck is empty." };
  }

  player.hand.push(drawnCard);
  state.canDrawChanceCard = false;
  state.lastChanceCard = drawnCard;
  record(state, () => `${player.name} drew chance card ${formatCard(drawnCard)}.`);

  let wastePickup = null;
  if (getPlayerValidMoves(state, player).length === 0) {
    const pickupResult = movePlayStackToPlayerHand(state, { playerName });
    if (pickupResult.ok) {
      wastePickup = pickupResult.wastePickup ?? null;
    }
  }

  refreshCpuMemoryForAll(state);
  const drawResult = { ok: true, message: "Chance card drawn.", card: drawnCard };
  if (wastePickup) {
    drawResult.wastePickup = wastePickup;
  }
  return drawResult;
}

export function pickUpWastepile(state, { playerName }) {
  if (!isPlayingPhase(state)) {
    return { ok: false, message: "Game is not in play." };
  }

  const player = assertCurrentPlayer(state, playerName);
  if (!state.canPickUpWastepile) {
    return { ok: false, message: "Cannot pick up wastepile after playing a card this turn." };
  }
  if (state.lastChanceCard && getPlayerValidMoves(state, player).some((card) => isSameCard(card, state.lastChanceCard))) {
    return { ok: false, message: "Chance card must be played before picking up wastepile." };
  }
  return movePlayStackToPlayerHand(state, { playerName: player.name });
}

export function openFaceDownCard(state, { playerName, cardId }) {
  if (!isPlayingPhase(state)) {
    return { ok: false, message: "Game is not in play." };
  }

  const player = assertCurrentPlayer(state, playerName);
  if (!onlyHasFaceDownCards(player)) {
    return { ok: false, message: "Face-down card can only be opened when hand and face-up are empty." };
  }
  if (state.activeFaceDownCard) {
    return { ok: false, message: "Only one face-down card can be active at a time." };
  }

  const cardInfo = findCardInPlayerCards(player, cardId);
  if (!cardInfo || cardInfo.zone !== "faceDown") {
    return { ok: false, message: "Face-down card not found." };
  }

  clearTableSlotCard(player, "faceDown", cardInfo.card.id, cardInfo.index);
  player.faceDown.splice(cardInfo.index, 1);
  player.hand.push(cardInfo.card);
  state.activeFaceDownCard = cardInfo.card;
  record(state, () => `${player.name} opened face-down card ${formatCard(cardInfo.card)}.`);

  const buildOpenFaceDownResult = (wastePickup = null) => {
    const result = { ok: true, message: "Face-down card opened." };
    if (wastePickup) {
      result.wastePickup = wastePickup;
    }
    return result;
  };

  const hasValidMoves = getPlayerValidMoves(state, player).length > 0;
  if (!hasValidMoves) {
    let wastePickup = null;
    if (state.cardsPlayedInTurn === 0) {
      const pickupResult = movePlayStackToPlayerHand(state, { playerName: player.name });
      if (pickupResult.ok) {
        wastePickup = pickupResult.wastePickup ?? null;
      }
    } else {
      wastePickup = nextTurn(state)?.wastePickup ?? null;
    }
    refreshCpuMemoryForAll(state);
    return buildOpenFaceDownResult(wastePickup);
  }

  if (state.cardsPlayedInTurn === 0) {
    refreshCpuMemoryForAll(state);
    return buildOpenFaceDownResult();
  }
  if (state.playStack.length === 0) {
    refreshCpuMemoryForAll(state);
    return buildOpenFaceDownResult();
  }
  if (cardInfo.card.rank === state.playStack[0].rank) {
    if (isOpenedFaceDownSameRankContinuation(state, cardInfo.card)) {
      state.nextCardMustBeSameRank = true;
    }
    refreshCpuMemoryForAll(state);
    return buildOpenFaceDownResult();
  }

  let wastePickup = null;
  if (state.playStack[0].rank !== RANK_RESET) {
    wastePickup = nextTurn(state)?.wastePickup ?? null;
  }
  refreshCpuMemoryForAll(state);
  return buildOpenFaceDownResult(wastePickup);
}

export function canSkipRemainingSameRank(state, { playerName }) {
  const current = getCurrentPlayer(state);
  return Boolean(isPlayingPhase(state) && current && current.name === playerName && state.nextCardMustBeSameRank);
}

export function skipRemainingSameRank(state, { playerName }) {
  if (!canSkipRemainingSameRank(state, { playerName })) {
    return { ok: false, message: "Skip is not allowed right now." };
  }

  state.nextCardMustBeSameRank = false;
  record(state, () => `${playerName} skipped remaining same-rank cards.`);
  const turnResult = nextTurn(state);
  refreshCpuMemoryForAll(state);
  const skipResult = { ok: true, message: "Skipped remaining same-rank cards." };
  if (turnResult?.wastePickup) {
    skipResult.wastePickup = turnResult.wastePickup;
  }
  return skipResult;
}

export function movePlayStackToPlayerHand(state, { playerName, forced = false }) {
  if (!isPlayingPhase(state)) {
    return { ok: false, message: "Game is not in play." };
  }

  const player = getPlayerByName(state, playerName);
  if (!player) {
    return { ok: false, message: "Player not found." };
  }
  if (state.playStack.length === 0) {
    return { ok: false, message: "Wastepile is empty." };
  }

  const pickedUpCards = [...state.playStack];
  const pickedUpCount = pickedUpCards.length;
  const topCard = pickedUpCards[0] ?? null;
  const wastePickup = {
    playerName: player.name,
    cardCount: pickedUpCount,
    cards: pickedUpCards,
    forced: Boolean(forced)
  };
  if (!isCpuPlayer(player)) {
    rememberHumanWastepilePickupForCpuPlayers(state, pickedUpCards);
  }
  player.hand.push(...pickedUpCards);
  state.playStack = [];
  record(
    state,
    () => `${player.name} picked up ${pickedUpCount} card${pickedUpCount === 1 ? "" : "s"} from the wastepile${
      topCard ? ` (top ${formatCard(topCard)})` : ""
    }.`
  );
  nextTurn(state);
  refreshCpuMemoryForAll(state);
  return {
    ok: true,
    message: "Wastepile moved to hand.",
    wastePickup
  };
}

function rememberHumanWastepilePickupForCpuPlayers(state, pickedUpCards) {
  if (!hasHumanPlayerForCpuMemory(state)) {
    return;
  }
  for (const cpuPlayer of getCpuPlayers(state)) {
    rememberHumanWastepilePickup(cpuPlayer, pickedUpCards);
  }
}

function rememberHumanRearrangedCardsForCpuPlayers(state, rearrangedCards) {
  if (!hasHumanPlayerForCpuMemory(state)) {
    return;
  }
  for (const cpuPlayer of getCpuPlayers(state)) {
    rememberHumanRearrangedCards(cpuPlayer, rearrangedCards);
  }
}

function rememberDiscardedCardsForCpuPlayers(state, discardedCards) {
  if (!hasHumanPlayerForCpuMemory(state)) {
    return;
  }
  for (const cpuPlayer of getCpuPlayers(state)) {
    rememberDiscardedCards(cpuPlayer, discardedCards);
  }
}

function hasHumanPlayerForCpuMemory(state) {
  return Array.isArray(state?.players) && state.players.some((player) => player?.role === PLAYER_ROLE_HUMAN);
}

export function getPlayerValidMoves(state, playerOrName) {
  if (!isPlayingPhase(state)) {
    return [];
  }

  const player = typeof playerOrName === "string" ? getPlayerByName(state, playerOrName) : playerOrName;
  if (!player) {
    return [];
  }
  const playableCards = getPlayableCards(player);
  return playableCards.filter((card) => isValidPlayableCard(state, card, playableCards));
}

export function isValidMove(state, player, card) {
  if (!isPlayingPhase(state) || !card) {
    return false;
  }

  const playableCards = getPlayableCards(player);
  return isValidPlayableCard(state, card, playableCards);
}

function isValidPlayableCard(state, card, playableCards) {
  if (!playableCards.some((playableCard) => playableCard.id === card.id)) {
    return false;
  }
  if (state.lastChanceCard && !isSameCard(state.lastChanceCard, card)) {
    return false;
  }
  if (state.playStack.length === 0) {
    return true;
  }

  const topCard = state.playStack[0];
  if (state.nextCardMustBeSameRank) {
    return card.rank === topCard.rank;
  }
  if (card.rank === RANK_RESET || card.rank === RANK_CLEAR) {
    return true;
  }
  return getRankValue(card) >= getRankValue(topCard);
}

export function nextTurn(state) {
  if (!isPlayingPhase(state)) {
    return { wastePickup: null };
  }

  if (activePlayers(state).length <= 1) {
    return { wastePickup: null };
  }

  const playerCount = state.players.length;
  const startIndex = state.currentPlayerIndex;
  let nextIndex = startIndex;

  for (let offset = 1; offset <= playerCount; offset += 1) {
    const candidateIndex = (startIndex + offset) % playerCount;
    const candidatePlayer = state.players[candidateIndex];
    if (candidatePlayer.status !== "Winner") {
      nextIndex = candidateIndex;
      break;
    }
  }

  state.currentPlayerIndex = nextIndex;
  resetTurnState(state);
  const nextPlayer = getCurrentPlayer(state);
  if (nextPlayer) {
    record(state, () => `Turn: ${nextPlayer.name}.`);
  }
  const wastePickup = takePlayStackWhenNoMoves(state);
  refreshCpuMemoryForAll(state);
  return { wastePickup };
}

export function checkWinCondition(state, player) {
  if (hasAnyCards(player)) {
    return false;
  }

  player.status = "Winner";
  record(state, () => `${player.name} became Winner.`);

  const remainingPlayers = state.players.filter((candidate) => hasAnyCards(candidate));
  if (remainingPlayers.length === 1) {
    remainingPlayers[0].status = "Idiot";
    record(state, () => `${remainingPlayers[0].name} is the Idiot.`);
  }

  return true;
}

export function takePlayStackWhenNoMoves(state) {
  if (!isPlayingPhase(state)) {
    return null;
  }

  const currentPlayer = getCurrentPlayer(state);
  if (!currentPlayer) {
    return null;
  }
  if (state.deck.length > 0 && state.canDrawChanceCard) {
    return null;
  }
  if (!hasAnyCards(currentPlayer)) {
    return null;
  }

  if (getPlayerValidMoves(state, currentPlayer).length === 0) {
    const pickupResult = movePlayStackToPlayerHand(state, {
      playerName: currentPlayer.name,
      forced: true
    });
    return pickupResult.ok ? (pickupResult.wastePickup ?? null) : null;
  }

  return null;
}

function beginPlayingPhase(state) {
  const fallbackIndex = state.currentPlayerIndex >= 0 ? state.currentPlayerIndex : 0;
  const fallbackPlayer = state.players[fallbackIndex] ?? state.players[0] ?? null;
  const startingPlayer = determineStartingPlayer(state.players, fallbackPlayer);
  const startingPlayerIndex = state.players.indexOf(startingPlayer);
  if (startingPlayerIndex >= 0) {
    state.currentPlayerIndex = startingPlayerIndex;
  }

  state.started = true;
  state.phase = PHASE_PLAYING;
  record(state, () => `Game started with ${state.players.length} players.`);
  const openingPlayer = getCurrentPlayer(state);
  if (openingPlayer) {
    record(state, () => `Turn: ${openingPlayer.name}.`);
  }
}

function isPlayingPhase(state) {
  return state.phase === PHASE_PLAYING;
}

function isGameOver(state) {
  return isPlayingPhase(state) && activePlayers(state).length <= 1;
}

function compareSetupCardStrength(a, b) {
  const strengthDelta = getSetupCardStrength(b) - getSetupCardStrength(a);
  if (strengthDelta !== 0) {
    return strengthDelta;
  }

  return String(a.id).localeCompare(String(b.id));
}

function getSetupCardStrength(card) {
  if (card.rank === RANK_CLEAR) {
    return 100;
  }
  if (card.rank === RANK_RESET) {
    return 99;
  }
  return getRankValue(card);
}

function activePlayers(state) {
  return state.players.filter((player) => player.status !== "Winner");
}

function cloneCardSpec(card) {
  return {
    id: card.id,
    suit: card.suit,
    rank: card.rank
  };
}

function selectCpuDecision(state, player, rng) {
  const actions = listCpuActions(state, player);
  if (actions.length === 0) {
    return null;
  }

  return chooseCpuAction({ state, player, actions, rng });
}

function listCpuActions(state, player) {
  const actions = [];
  const currentZone = getPlayableZone(player);

  if (currentZone === "faceDown" && onlyHasFaceDownCards(player)) {
    for (const card of player.faceDown) {
      actions.push({ type: "openFaceDownCard", cardId: card.id, zone: "faceDown" });
    }
  } else {
    const validMoves = getPlayerValidMoves(state, player);
    for (const card of validMoves) {
      actions.push({ type: "playCard", cardId: card.id, zone: currentZone });
    }
  }

  if (canSkipRemainingSameRank(state, { playerName: player.name })) {
    actions.push({ type: "skipRemainingSameRank" });
  }
  if (!state.nextCardMustBeSameRank && state.canDrawChanceCard && state.deck.length > 0) {
    actions.push({ type: "drawChanceCard" });
  }
  if (state.canPickUpWastepile && state.playStack.length > 0) {
    actions.push({ type: "pickUpWastepile" });
  }

  return actions;
}

function applyCpuDecision(state, player, decision) {
  switch (decision.type) {
    case "playCard":
      return playCard(state, { playerName: player.name, cardId: decision.cardId });
    case "openFaceDownCard":
      return openFaceDownCard(state, { playerName: player.name, cardId: decision.cardId });
    case "skipRemainingSameRank":
      return skipRemainingSameRank(state, { playerName: player.name });
    case "drawChanceCard":
      return drawChanceCard(state, { playerName: player.name });
    case "pickUpWastepile":
      return pickUpWastepile(state, { playerName: player.name });
    default:
      return { ok: false, message: "CPU selected an unknown action." };
  }
}

function drawToHand(player, state) {
  if (player.hand.length >= 3) {
    return;
  }
  const card = drawCardFromDeck(state.deck);
  if (card) {
    player.hand.push(card);
  }
}

function hasCardsOfSameRank(player, card) {
  if (getPlayableZone(player) === "faceDown") {
    return false;
  }

  return getPlayableCards(player).some((candidate) => candidate.rank === card.rank);
}

function isOpenedFaceDownSameRankContinuation(state, card) {
  const topCard = state.playStack[0] ?? null;
  return Boolean(
    state.cardsPlayedInTurn > 0
    && topCard
    && topCard.rank !== RANK_RESET
    && topCard.rank !== RANK_CLEAR
    && card.rank === topCard.rank
  );
}

function isFourOfAKind(playStack) {
  if (playStack.length < 4) {
    return false;
  }
  const topRank = playStack[0].rank;
  return playStack.slice(0, 4).every((card) => card.rank === topRank);
}

function assertCurrentPlayer(state, playerName) {
  const currentPlayer = getCurrentPlayer(state);
  if (!currentPlayer || currentPlayer.name !== playerName) {
    throw new Error(`It's not ${playerName}'s turn.`);
  }
  return currentPlayer;
}

function resetTurnState(state) {
  resetTurnStateCommon(state);
  state.canPickUpWastepile = true;
  state.cardsPlayedInTurn = 0;
  state.lastChanceCard = null;
}

function resetTurnStateSoft(state) {
  resetTurnStateCommon(state);
  state.canPickUpWastepile = false;
  state.cardsPlayedInTurn += 1;
}

function resetTurnStateCommon(state) {
  state.canDrawChanceCard = true;
  state.nextCardMustBeSameRank = false;
  state.activeFaceDownCard = null;
}

function record(state, message) {
  if (state.suppressLogMessages) {
    return;
  }

  const renderedMessage = typeof message === "function" ? message() : message;
  state.log.push(renderedMessage);

  if (state.log.length > LOG_LIMIT) {
    state.log.splice(0, state.log.length - LOG_LIMIT);
  }
}

function formatCard(card) {
  return `${card.rank}${SUIT_SYMBOLS[card.suit] ?? ""}`;
}

function formatZone(zone) {
  if (zone === "faceUp") {
    return "face-up";
  }
  if (zone === "faceDown") {
    return "face-down";
  }
  return "hand";
}
